import React, { useEffect, useRef, useState } from "react";
import LoadingOverlay from "react-loading-overlay";
import { RecommendationBand as LeaseBand } from "../../recommendations/components/Bands/RecommendationBand";
import { FormField } from "../../recommendations/components/Bands/FormField";
import { DYNAMIC_FIELD_TYPES } from "../../common/constants";
import { useDispatch, useSelector } from "react-redux";
import ConfirmationModal from "../../common/components/ConfirmationModal";
import Portal from "../../common/components/Portal";
import Loader from "../../common/components/Loader";
import _ from "lodash";
import actions from "../actions";
import buildingAction from "../../building/actions";
import buildingAdditionAction from "../../buildingAddition/actions";
import ReactTooltip from "react-tooltip";
import useLeaseForm from "./useLeaseForm";
import { useParams, useLocation } from "react-router-dom/cjs/react-router-dom.min";
import qs from "query-string";
import moment from "moment";
import { FilterSearch } from "../../mobileRecommendation/components/FilterModal/FilterSearch";
import { DynamicBand } from "../../recommendations/components/Bands/DynamicBand";

const LeaseForm = ({ basicDetails, isLoading }) => {
    const {
        data,
        handleChange,
        handleSubmit,
        errorParams,
        showErrorBorder,
        onCancel,
        setShowCancelConfirmModal,
        showCancelConfirmModal,
        cancelForm,
        initialData,
        fieldSearch,
        setFieldSearch
    } = useLeaseForm({
        initialState: basicDetails
    });
    const dispatch = useDispatch();
    const { id: lease_id } = useParams();
    const query = qs.parse(useLocation()?.search);
    const [showBand, setShowBand] = useState({ band101: true });
    const { getLeaseBuildingByIdLoading } = useSelector(state => state.leaseBuildingReducer);
    const [yearFields, setYearFields] = useState([]);

    const leaseDetailMainFields = [
        {
            label: "Name",
            key: "name",
            type: DYNAMIC_FIELD_TYPES.TEXT,
            mandatory: true
        },
        {
            label: "Description",
            key: "description",
            type: DYNAMIC_FIELD_TYPES.TEXT,
            mandatory: false
        },
        {
            label: "Color",
            key: "color_code",
            type: DYNAMIC_FIELD_TYPES.COLOR,
            mandatory: false
        }
    ];

    const LeaseDetailRestFields = [
        {
            label: "Start Date",
            key: "start_date",
            type: DYNAMIC_FIELD_TYPES.DATE,
            mandatory: true
        },
        {
            label: "End Date",
            key: "end_date",
            type: DYNAMIC_FIELD_TYPES.DATE,
            mandatory: true
        },
        {
            label: "Building Description",
            key: "building_description",
            type: DYNAMIC_FIELD_TYPES.TEXT,
            mandatory: false,
            isReadOnly: true,
            accessor: data => (buildings.length && data.building_id ? buildings.find(b => b.id === data.building_id)?.building_description : ""),
            hasCopy: true
        },
        {
            label: "Building SF",
            key: "building_sf",
            type: DYNAMIC_FIELD_TYPES.NUMBER,
            mandatory: false,
            isReadOnly: true,
            thousandSeparator: true,
            accessor: data => (buildings.length && data.building_id ? buildings.find(b => b.id === data.building_id)?.area : "")
        }
    ];
    useEffect(() => {
        return () => {
            dispatch(actions?.resetReducer());
        };
    }, []);

    const renderCancelConfirmationModal = () => {
        if (!showCancelConfirmModal) return null;
        return (
            <Portal
                body={
                    <ConfirmationModal
                        type="cancel"
                        heading={"Do you want to clear and lose all changes?"}
                        message={"This action cannot be reverted, are you sure that you need to cancel?"}
                        onNo={() => setShowCancelConfirmModal(false)}
                        onYes={cancelForm}
                    />
                }
                onCancel={() => setShowCancelConfirmModal(false)}
            />
        );
    };

    const renderStaticFields = (eachField, eachBand, index) => {
        const value = eachField.isReadOnly ? eachField.accessor(data) : data?.[eachField?.key];
        const dateFields = ["start_date", "end_date"];
        const errorValue = Object.keys(errorParams || {});

        return (
            <FormField
                label={eachField?.label}
                fieldKey={eachField?.key}
                fieldParam={eachField?.key}
                fieldType={eachField.type}
                value={value}
                bandId={`band${index + 1}`}
                bandShown={showBand}
                handleBandClick={(band, value) => setShowBand({ ...showBand, [band]: value })}
                isAsset
                suggestionArray={[]}
                showErrorBorder={showErrorBorder && errorValue?.some(e => e === eachField?.key)}
                onChange={e => {
                    !eachField.isReadOnly &&
                        handleChange(
                            {
                                name: eachField?.key,
                                value: dateFields?.includes(eachField?.key)
                                    ? moment(e)?.format("YYYY-MM-DD")
                                    : eachField.type === DYNAMIC_FIELD_TYPES.COLOR
                                    ? e
                                    : e?.target?.value
                            },
                            "",
                            true
                        );
                }}
                fieldItem={{
                    rules: {
                        mandatory: eachField?.mandatory,
                        read_only: eachField?.isReadOnly,
                        has_copy: eachField?.hasCopy,
                        thousand_separator: eachField?.thousandSeparator
                    }
                }}
                isLease
            />
        );
    };

    const renderYearField = (year, bandId) => (
        <FormField
            key={year.key}
            label={year.label}
            placeholder={"Enter SF"}
            fieldKey={year.key}
            fieldParam={`years.${year.key}`}
            fieldType={DYNAMIC_FIELD_TYPES.NUMBER}
            value={data?.sf_years?.[year.key]?.value || ""}
            bandId={bandId}
            isAsset
            bandShown={showBand}
            handleBandClick={(band, value) => setShowBand({ ...showBand, [band]: value })}
            onChange={(e, value) => {
                handleChange(
                    {
                        name: `years.${year.key}`,
                        value: {
                            label: year.label,
                            value: value ?? e?.value ?? ""
                        }
                    },
                    "",
                    true,
                    false
                );
            }}
            fieldItem={{
                rules: {
                    suffix: " SF",
                    thousand_separator: true,
                    decimal_scale: 2,
                    allow_negative: false,
                    mandatory: true
                }
            }}
            showErrorBorder={showErrorBorder && errorParams?.[`${year.key}`]}
        />
    );

    const generateYearRanges = (startDate, endDate) => {
        const ranges = [];
        const start = moment(startDate, "YYYY-MM-DD");
        const end = moment(endDate, "YYYY-MM-DD");

        if (!start.isValid() || !end.isValid() || end.isBefore(start)) return [];

        let year = 1;
        let currentStart = moment(start);

        while (currentStart.isBefore(end)) {
            const tentativeEnd = moment(currentStart).add(1, "year").subtract(1, "day");
            const currentEnd = tentativeEnd.isAfter(end) ? end : tentativeEnd;

            ranges.push({
                label: `Year ${year} (${currentStart.format("MMM D, YYYY")} - ${currentEnd.format("MMM D, YYYY")})`,
                key: `year_${year}`,
                start: currentStart.clone(),
                end: currentEnd.clone()
            });

            currentStart = moment(currentEnd).add(1, "day");
            year++;
        }

        return ranges;
    };

    useEffect(() => {
        const start = data?.start_date;
        const end = data?.end_date;
        if (start && end) {
            const years = generateYearRanges(start, end);
            setYearFields(years);

            const next = {};
            years.forEach(y => {
                const existing = data?.sf_years?.[y.key]?.value ?? "";
                next[y.key] = { label: y.label, value: existing };
            });

            handleChange({ name: "sf_years", value: next }, "", true, false);
        }
    }, [data?.start_date, data?.end_date]);

    const { getRegionsBasedOnClientResponse: { regions = [] } = {}, getSitesBasedOnRegionResponse: { sites = [] } = {} } = useSelector(
        state => state.buildingReducer || {}
    );
    const { getAllBuildingsDropdownResponse: { buildings = [] } = {} } = useSelector(state => state.buildingAdditionReducer || {});

    useEffect(() => {
        dispatch(buildingAction.getRegionsBasedOnClient(query?.cid));
    }, []);

    useEffect(() => {
        if (data?.region_id) {
            dispatch(buildingAction.getSitesBasedOnRegion(data?.region_id));
        }
    }, [data?.region_id]);

    useEffect(() => {
        if (data?.site_id) {
            dispatch(buildingAdditionAction.getAllBuildingsDropdown({ site_id: data?.site_id, project_id: query?.pid }));
        }
    }, [data?.site_id]);

    const renderSelectField = ({ label, key, bandId, options, mandatory, isReadOnly }) => (
        <FormField
            key={key}
            label={label}
            fieldKey={key}
            fieldParam={key}
            fieldType={DYNAMIC_FIELD_TYPES.SELECT}
            value={data?.[key] || ""}
            bandId={bandId}
            bandShown={showBand}
            handleBandClick={(band, value) => setShowBand({ ...showBand, [band]: value })}
            showErrorBorder={showErrorBorder && Object.keys(errorParams || {}).some(e => e === key)}
            onChange={val => {
                if (key === "region_id") {
                    handleChange({ name: "region_id", value: val }, "", true);
                } else if (key === "site_id") {
                    handleChange({ name: "site_id", value: val }, "", true);
                } else {
                    handleChange({ name: key, value: val }, "", true);
                }
            }}
            fieldItem={{
                rules: { mandatory: true, read_only: isReadOnly },
                field_options: options
            }}
            isAsset
        />
    );

    return (
        <LoadingOverlay active={isLoading || getLeaseBuildingByIdLoading} spinner={<Loader />} fadeSpeed={10}>
            {renderCancelConfirmationModal()}
            <div className="dtl-sec col-md-12">
                <div className="tab-dtl region-mng">
                    <form autoComplete={"nope"}>
                        <ReactTooltip id="survey-edit" effect="solid" place="top" backgroundColor="#007bff" className="rc-tooltip-custom-class" />
                        <div className="tab-dtl region-mng additional-dtl addition-edit">
                            <div className="tab-active location-sec recom-sec main-dtl-add recommendation-form add-recommendation edit-mob-recom lease_mng">
                                <div class="header-mod-top d-flex edit-asset-mob">
                                    <div className="d-flex">
                                        <div className="search search-box-asset mr-2 mt-2 not-draggable">
                                            <FilterSearch
                                                fieldSearch={fieldSearch}
                                                setFieldSearch={setFieldSearch}
                                                fields={Object.values(basicDetails ?? {}).reduce((acc, item) => {
                                                    acc = [...acc, ...Object.keys(item ?? {}).map(e => ({ label: item?.[e]?.label }))];
                                                    return acc;
                                                }, [])}
                                            />
                                        </div>
                                    </div>
                                    <div className="text-right btnOtr edit-cmn-btn-outer btn-mob-rcm">
                                        <span className="errorMessage">
                                            {showErrorBorder && Object.keys(errorParams)?.length ? Object.values(errorParams)[0] : ""}
                                        </span>
                                        <button type="button" className="btn btn-secondary btnClr mr-1" onClick={onCancel}>
                                            Cancel
                                        </button>
                                        {false ? (
                                            <button type="button" className="btn btn-primary btnRgion ml-2 cursor-diabled">
                                                <div className="button-loader d-flex justify-content-center align-items-center">
                                                    <div className="spinner-border text-white" role="status">
                                                        <span className="sr-only">Loading...</span>
                                                    </div>
                                                </div>
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                className={`btn btn-primary btnRgion  ${
                                                    _.isEqual(initialData, data) && lease_id ? "cursor-diabled" : ""
                                                }`}
                                                disabled={lease_id ? _.isEqual(initialData, data) : false}
                                                onClick={() => {
                                                    handleSubmit();
                                                }}
                                            >
                                                {lease_id ? "Update" : "Submit"}
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="col-md-12 detail-recom add-details-outer">
                                    <div className="outer-rcm recommendations suggst-drop-icon">
                                        <div className="cnt-sec">
                                            <div className="row">
                                                <div className="col-md-12 pr-0 asset-edit-port">
                                                    <div id="accordion">
                                                        <>
                                                            <div className="d-flex mb-2">
                                                                <div className="col-md-12 p-0">
                                                                    <LeaseBand
                                                                        label={"Basic Details"}
                                                                        bandId={`band${100 + 1}`}
                                                                        handleBandClick={(band, value) => setShowBand({ ...showBand, [band]: value })}
                                                                        isDefaultOpen
                                                                        showBand={showBand}
                                                                        MainFields={leaseDetailMainFields.map((eachField, index) => {
                                                                            return renderStaticFields(eachField, "Basic Details", index);
                                                                        })}
                                                                        RestFields={LeaseDetailRestFields.map((eachField, index) => {
                                                                            return renderStaticFields(eachField, "Basic Details", index);
                                                                        })}
                                                                    />
                                                                    <LeaseBand
                                                                        label={"Geo Hierarchy"}
                                                                        bandId={`band150`}
                                                                        handleBandClick={(band, value) => setShowBand({ ...showBand, [band]: value })}
                                                                        isDefaultOpen
                                                                        showBand={showBand}
                                                                        MainFields={[
                                                                            renderSelectField({
                                                                                label: "Region",
                                                                                key: "region_id",
                                                                                bandId: "band150",
                                                                                options: regions,
                                                                                mandatory: true,
                                                                                isReadOnly: query?.region_id
                                                                            }),
                                                                            renderSelectField({
                                                                                label: "Site",
                                                                                key: "site_id",
                                                                                bandId: "band150",
                                                                                options: data.region_id ? sites : [],
                                                                                mandatory: true,
                                                                                isReadOnly: query?.site_id
                                                                            }),
                                                                            renderSelectField({
                                                                                label: "Building",
                                                                                key: "building_id",
                                                                                bandId: "band150",
                                                                                options: data.region_id && data.site_id ? buildings : [],
                                                                                mandatory: true,
                                                                                isReadOnly: query?.bid
                                                                            })
                                                                        ]}
                                                                    />
                                                                    {yearFields.length > 0 && (
                                                                        <LeaseBand
                                                                            label={"SF/Year"}
                                                                            bandId={`band201`}
                                                                            handleBandClick={(band, value) =>
                                                                                setShowBand({ ...showBand, [band]: value })
                                                                            }
                                                                            isDefaultOpen
                                                                            showBand={showBand}
                                                                            MainFields={yearFields
                                                                                .slice(0, 3)
                                                                                .map(year => renderYearField(year, "band201"))}
                                                                            RestFields={yearFields
                                                                                .slice(3)
                                                                                .map(year => renderYearField(year, "band201"))}
                                                                        />
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </>
                                                        <DynamicBand
                                                            bandData={data?.dynamic_fields}
                                                            bandShown={showBand}
                                                            handleBandClick={(band, value) => setShowBand({ ...showBand, [band]: value })}
                                                            handleChangeData={band => {
                                                                handleChange(band, "", false, true);
                                                            }}
                                                            showErrorBorder={showErrorBorder}
                                                            isLease={true}
                                                            searchField={fieldSearch.toLowerCase()}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </LoadingOverlay>
    );
};

export default LeaseForm;
